// Client-side "resize before upload" engine. Downscales + re-encodes JPEGs to a
// bounded long edge in the browser BEFORE they reach the upload manager, keeping
// full EXIF (date/camera/GPS/copyright). The heavy work runs in a Web Worker
// (src/lib/image/resizeImage.worker.ts) so a big batch never janks the admin;
// when Worker/OffscreenCanvas is unavailable it falls back to the same pure core
// (resizeCore.ts) on the main thread.
//
// This module is deliberately DECOUPLED from the upload manager: it turns a set of
// picked Files into a set of resized Files with identical names (the backend keys
// on filename) and preserved folder paths, which the dialog then hands to the
// existing `enqueue` unchanged. When the "Original" preset is chosen this is never
// invoked, so behaviour is byte-for-byte identical to today.
import { isResizeUnsupported, resizeFileToBlob, type ResizeOpts } from "./resizeCore";
import { readJpegHeader } from "./scaledDecode";
import { cutCrops, type CropRequest, type CutCrop } from "./cropCore";
import { registerUploadProbe } from "@/lib/diag/uploadDiag";
import { previewSpeedOn } from "@/lib/uploader/previewSpeed";

export type { ResizeOpts };

interface WorkerResponse {
  id: number;
  kind: "blob" | "original" | "error" | "crops";
  blob?: Blob;
  crops?: CutCrop[] | null;
  error?: string;
  /** This browser can't make the copy (no OffscreenCanvas / decode route / JPEG encoder). */
  unsupported?: boolean;
}

interface Pending {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve: (value: any) => void;
  reject: (err: Error) => void;
}

// A POOL of module workers, so several photos decode/encode in parallel (one worker ran every resize
// one after another — ~3–5 photos/s, which capped a preview-first upload at ~20 Mbps whatever the
// connection).
//
// One worker per core. A preview copy is CPU-bound — ~150–200 core-ms for a 24 MP original (~120 ms of
// it decoding the full JPEG's entropy data, which a reduced-size decode can't skip) — and the preview
// phase can never go faster than this pool turns originals into copies: the uploads in flight settle
// at (copies per second) × (request time), which is why previews showed only ~4–6 uploads at once
// even over HTTP/2. The old pool (cores − 1, at most 4) left a core idle on every machine and half of
// an 8-core one. The page's own work per photo is small now that landed files re-render on the
// throttled flush (useUploadManager), so it keeps up while sharing the cores. Capped at 8 to bound the
// decoded bitmaps in flight.
//
// localStorage `ph_resize_pool`: a number (1–16) pins the pool size; `legacy` restores the old sizing
// (as does the preview-speed kill switch, `ph_preview_speed=0`). Read once, when the page loads.
export const resizePoolSize = (hardwareConcurrency: number | undefined, override?: string | null): number => {
  const cores = hardwareConcurrency && hardwareConcurrency > 0 ? hardwareConcurrency : 2;
  if (override === "legacy") return Math.max(1, Math.min(4, cores - 1));
  const pinned = Number(override);
  if (override && Number.isInteger(pinned) && pinned >= 1) return Math.min(16, pinned);
  return Math.max(2, Math.min(8, cores));
};

const poolOverride = (): string | null => {
  if (!previewSpeedOn()) return "legacy";
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem("ph_resize_pool") : null;
  } catch {
    return null; // sandboxed / private mode — default sizing
  }
};

export const RESIZE_PARALLEL = resizePoolSize(
  typeof navigator !== "undefined" ? navigator.hardwareConcurrency : undefined,
  poolOverride(),
);

// Resizes in flight per worker. One resize alone leaves its core idle part of the time — while Chrome
// decodes the original on its image thread pool, reads the file, and hands the canvas to the GPU
// process — so with one each the pool kept only ~4.4 of 8 cores busy (staging bench). A second one per
// worker fills those gaps. localStorage `ph_resize_inflight` (1–4) overrides it; 1 with the kill switch.
export const resizeInflightPerWorker = (override?: string | null, legacy = false): number => {
  if (legacy) return 1;
  const v = Number(override);
  if (override && Number.isInteger(v) && v >= 1) return Math.min(4, v);
  return 2;
};

const inflightOverride = (): string | null => {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem("ph_resize_inflight") : null;
  } catch {
    return null;
  }
};

/** How many resizes callers may have in flight at once across the pool (workers × per worker). */
export const RESIZE_CONCURRENCY = RESIZE_PARALLEL * resizeInflightPerWorker(inflightOverride(), !previewSpeedOn());

interface PoolWorker {
  w: Worker;
  busy: number;
}

// `undefined` = not yet attempted; `null` = unavailable (fall back to main thread).
let pool: PoolWorker[] | null | undefined;
let reqId = 0;
const pending = new Map<number, Pending & { pw: PoolWorker; bytes: number }>();

// Diagnostics (gated): confirm the resize pool stays bounded — at most RESIZE_CONCURRENCY files are
// ever decoding at once, so this should never be the source of a multi-GB wedge.
registerUploadProbe("resize", () => {
  let bytes = 0;
  for (const p of pending.values()) bytes += p.bytes || 0;
  return { items: pending.size, bytes, note: `workers=${pool ? pool.length : 0}` };
});

function failPending(pw: PoolWorker, err: Error): void {
  for (const [id, p] of pending) {
    if (p.pw !== pw) continue;
    pending.delete(id);
    p.reject(err);
  }
}

function spawnWorker(): PoolWorker | null {
  try {
    const w = new Worker(new URL("./resizeImage.worker.ts", import.meta.url), { type: "module" });
    const pw: PoolWorker = { w, busy: 0 };
    w.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const m = e.data;
      const p = pending.get(m.id);
      if (!p) return;
      pending.delete(m.id);
      pw.busy -= 1;
      if (m.kind === "error") {
        const err = new Error(m.error || "resize failed");
        if (m.unsupported) err.name = "ResizeUnsupportedError";
        p.reject(err);
      }
      else if (m.kind === "crops") p.resolve(m.crops ?? null);
      else p.resolve(m.kind === "blob" ? m.blob ?? null : null);
    };
    // A worker crash rejects what it had outstanding (callers fall back to the main thread) and
    // drops it from the pool; the rest keep going.
    w.onerror = () => {
      failPending(pw, new Error("resize worker crashed"));
      if (pool) pool = pool.filter((x) => x !== pw);
      if (pool && !pool.length) pool = null;
    };
    return pw;
  } catch {
    return null;
  }
}

/** Tear the resize pool down, terminating every worker and rejecting anything still
 *  outstanding, so a torn-down batch doesn't leave workers — and the decoded bitmaps /
 *  canvases they were mid-resize on — resident. In-flight callers fall back to the
 *  main-thread core (or, when cancelled, simply stop). The pool rebuilds lazily on the next
 *  resize. Called when the upload queue goes idle (e.g. a large batch is cancelled). */
export function shutdownResizePool(): void {
  const err = new Error("resize pool shut down");
  for (const [id, p] of pending) {
    pending.delete(id);
    try { p.reject(err); } catch { /* already settled */ }
  }
  if (pool) for (const pw of pool) {
    try { pw.w.terminate(); } catch { /* already gone */ }
  }
  pool = undefined; // `undefined` (not `null`) → lazily rebuilt on the next resize
}

/** The least-busy pool worker (pool created lazily). Null when workers are unavailable. */
function getWorker(): PoolWorker | null {
  if (pool === undefined) {
    if (typeof Worker === "undefined" || typeof window === "undefined") {
      pool = null;
    } else {
      const made = Array.from({ length: RESIZE_PARALLEL }, spawnWorker).filter((x): x is PoolWorker => !!x);
      pool = made.length ? made : null;
    }
  }
  if (!pool) return null;
  return pool.reduce((a, b) => (b.busy < a.busy ? b : a));
}

function postToWorker(pw: PoolWorker, file: File, opts: ResizeOpts): Promise<Blob | null> {
  return new Promise<Blob | null>((resolve, reject) => {
    const id = (reqId += 1);
    pending.set(id, { resolve, reject, pw, bytes: file.size || 0 });
    pw.busy += 1;
    pw.w.postMessage({ id, kind: "resize", file, maxEdge: opts.maxEdge, quality: opts.quality });
  });
}

function postCropToWorker(pw: PoolWorker, file: File, items: CropRequest[]): Promise<CutCrop[] | null> {
  return new Promise<CutCrop[] | null>((resolve, reject) => {
    const id = (reqId += 1);
    pending.set(id, { resolve, reject, pw, bytes: file.size || 0 });
    pw.busy += 1;
    pw.w.postMessage({ id, kind: "crop", file, items });
  });
}

/**
 * Crop-back: cut the server's requested crops from an ORIGINAL file (cropCore.cutCrops) on the shared
 * resize pool, so it never janks the page and shares the pool's bounded memory. Null = this original
 * can't be used (not a JPEG, won't decode); the server then keeps its own crops.
 */
export async function cutCropsFromOriginal(file: File, items: CropRequest[]): Promise<CutCrop[] | null> {
  const w = getWorker();
  if (w) {
    try {
      return await postCropToWorker(w, file, items);
    } catch {
      // fall through to the main-thread core
    }
  }
  try {
    return await cutCrops(file, items);
  } catch {
    return null;
  }
}

/** Resize one file to a Blob (null = keep the original: not a JPEG, or it already fits), via a worker,
 *  then the same core on the main thread. Throws when neither can make it. */
async function resizeToBlobOrThrow(file: File, opts: ResizeOpts): Promise<Blob | null> {
  if (file.type !== "image/jpeg") return null;
  const w = getWorker();
  if (w) {
    try {
      return await postToWorker(w, file, opts);
    } catch (err) {
      // This browser can't make it (the main thread has the same engine) — say so; a crashed or
      // shut-down worker falls through to the main-thread core.
      if (isResizeUnsupported(err)) throw err;
    }
  }
  return resizeFileToBlob(file, opts);
}

/** Resize one file to a Blob (or null = keep original), via worker then main-thread. */
async function resizeToBlob(file: File, opts: ResizeOpts): Promise<Blob | null> {
  try {
    return await resizeToBlobOrThrow(file, opts);
  } catch {
    return null; // never lose a file — keep the original
  }
}

/** A preview-first copy couldn't be made in this browser. The item fails with this reason — the original is
 *  never sent in the copy's place (it would go live as the "preview" and cost the copy pass its speed). */
export class PreviewCopyError extends Error {
  /** `causeName`: the underlying error's name (e.g. ResizeUnsupportedError, NotReadableError) — counted per batch. */
  constructor(public readonly detail: string, public readonly causeName = "unknown") {
    super("Couldn't make a preview copy in this browser");
    this.name = "PreviewCopyError";
  }
}

/**
 * The preview-first copy of one photo: the resized JPEG, or the file itself when there's nothing to make
 * (not a JPEG, or already within `opts.maxEdge`). Throws PreviewCopyError when this browser can't make it.
 */
export async function resizeForCopy(file: File, opts: ResizeOpts): Promise<File> {
  let blob: Blob | null;
  try {
    blob = await resizeToBlobOrThrow(file, opts);
  } catch (err) {
    const e = err as { name?: unknown } | null;
    const causeName = e && typeof e.name === "string" && e.name !== "Error" ? e.name : "unknown";
    throw new PreviewCopyError(err instanceof Error ? err.message : String(err), causeName);
  }
  return blob ? toFile(blob, file) : file;
}

// ---- Preview-copy probe ------------------------------------------------------------------------------
// A 32x16 JPEG stored on its side with EXIF orientation 6 (upright it is 16x32): red half / blue half.
const PROBE_JPEG_B64 =
  "/9j/4AAQSkZJRgABAQAAAQABAAD/4QA4RXhpZgAATU0AKgAAAAgAAgEPAAIAAAAKAAAAJgESAAMAAAABAAYAAAAAAABQaG90b2hhd2sA/9sAQwAGBAUGBQQGBgUGBwcGCAoQCgoJCQoUDg8MEBcUGBgXFBYWGh0lHxobIxwWFiAsICMmJykqKRkfLTAtKDAlKCko/9sAQwEHBwcKCAoTCgoTKBoWGigoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgo/8AAEQgAEAAgAwEiAAIRAQMRAf/EAB8AAAEFAQEBAQEBAAAAAAAAAAABAgMEBQYHCAkKC//EALUQAAIBAwMCBAMFBQQEAAABfQECAwAEEQUSITFBBhNRYQcicRQygZGhCCNCscEVUtHwJDNicoIJChYXGBkaJSYnKCkqNDU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6g4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2drh4uPk5ebn6Onq8fLz9PX29/j5+v/EAB8BAAMBAQEBAQEBAQEAAAAAAAABAgMEBQYHCAkKC//EALURAAIBAgQEAwQHBQQEAAECdwABAgMRBAUhMQYSQVEHYXETIjKBCBRCkaGxwQkjM1LwFWJy0QoWJDThJfEXGBkaJicoKSo1Njc4OTpDREVGR0hJSlNUVVZXWFlaY2RlZmdoaWpzdHV2d3h5eoKDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uLj5OXm5+jp6vLz9PX29/j5+v/aAAwDAQACEQMRAD8A4iiiivmj9tORooor+kj8BP/Z";

const probeFile = (): File => {
  const bin = atob(PROBE_JPEG_B64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], "probe.jpg", { type: "image/jpeg" });
};

export type ResizeProbe = { ok: true; ms: number } | { ok: false; reason: string };

/**
 * Can this browser make preview copies? Resizes the tiny EXIF-6 test JPEG through the real path a copy takes
 * (a resize worker: decode, orientation, OffscreenCanvas, JPEG encode, EXIF) and checks the result is an
 * upright 8x16 JPEG. Never throws.
 */
export async function probeWorkerResize(timeoutMs = 5000): Promise<ResizeProbe> {
  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
  const w = getWorker();
  if (!w) return { ok: false, reason: "no resize worker in this browser" };
  try {
    const blob = await Promise.race([
      postToWorker(w, probeFile(), { maxEdge: 16, quality: 0.8 }),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`no answer in ${timeoutMs} ms`)), timeoutMs)),
    ]);
    if (!blob) return { ok: false, reason: "the worker made no copy" };
    if (blob.type !== "image/jpeg") return { ok: false, reason: `the copy is ${blob.type || "of an unknown type"}, not JPEG` };
    const h = await readJpegHeader(blob);
    if (!h) return { ok: false, reason: "the copy isn't a readable JPEG" };
    if (h.width !== 8 || h.height !== 16) return { ok: false, reason: `the copy is ${h.width}x${h.height}, not an upright 8x16` };
    return { ok: true, ms: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - t0) };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
}

/** Wrap a resized Blob into a File that mimics the original (name, mtime, folder path). */
function toFile(blob: Blob, original: File): File {
  const out = new File([blob], original.name, {
    type: "image/jpeg",
    lastModified: original.lastModified,
  });
  // Folder uploads mirror webkitRelativePath into the S3 key prefix; carry it over
  // (File() doesn't preserve it) exactly as the dialog stamps dropped folders.
  const rel = (original as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (rel && rel !== original.name) {
    try {
      Object.defineProperty(out, "webkitRelativePath", { value: rel, configurable: true });
    } catch {
      /* some environments freeze File — fall back to the flat name */
    }
  }
  return out;
}

/**
 * Resize a single JPEG to a new JPEG File, retaining EXIF. Non-JPEGs, already-small
 * images, and any failure path return the ORIGINAL File untouched.
 */
export async function resizeToJpeg(file: File, opts: ResizeOpts): Promise<File> {
  const blob = await resizeToBlob(file, opts);
  return blob ? toFile(blob, file) : file;
}

export interface ResizeBatchOptions {
  /** Max concurrent resizes (bounds memory). Default 4. */
  concurrency?: number;
  /** Progress callback: (completed, total). */
  onProgress?: (done: number, total: number) => void;
  /** Abort the batch (e.g. the dialog closed). Rejects with an AbortError. */
  signal?: AbortSignal;
}

/**
 * Resize a mixed batch, preserving order and length. Non-JPEGs (videos, etc.) pass
 * through untouched. A small concurrency pool bounds peak memory. Cancelable via
 * `signal` — an abort rejects with a DOMException('AbortError').
 */
export async function resizeManyToJpeg(
  files: File[],
  opts: ResizeOpts,
  { concurrency = 4, onProgress, signal }: ResizeBatchOptions = {},
): Promise<File[]> {
  const total = files.length;
  const out = new Array<File>(total);
  let done = 0;
  let next = 0;
  onProgress?.(0, total);

  const abortError = () => new DOMException("Resize aborted", "AbortError");
  if (signal?.aborted) throw abortError();

  const runLane = async (): Promise<void> => {
    for (;;) {
      if (signal?.aborted) return;
      const i = next;
      next += 1;
      if (i >= total) return;
      let result: File;
      try {
        result = await resizeToJpeg(files[i], opts);
      } catch {
        result = files[i]; // never lose a file
      }
      out[i] = result;
      done += 1;
      onProgress?.(done, total);
    }
  };

  const lanes = Math.max(1, Math.min(concurrency, total || 1));
  await Promise.all(Array.from({ length: lanes }, () => runLane()));

  if (signal?.aborted) throw abortError();
  return out;
}
